import { useCallback, useEffect, useState } from 'react';
import * as turf from '@turf/turf';
import type { Feature, Point } from 'geojson';
import { anyType } from '../types';
import { geojson, INITIAL_DISTANCE_LABEL, lineString } from '../constants/components/measureDistanceControl';
import { hiddenMeasureLabel, showMeasureLabel } from '../lib/measureControl';
import { getLayersBySourceId, updateResultLabelSource } from '../lib/mapUtils';


export const DISTANCE_SOURCE = 'distance-measurement';
export const DISTANCE_POINT_LAYER = 'distance-measurement/result-point';


export const useMeasureDistanceControl = (map: maplibregl.Map | null, isOpen: boolean) => {

  const [distanceLabel, setDistanceLabel] = useState(INITIAL_DISTANCE_LABEL);

  /* ****************
   * マップクリック時の処理
   * ****************/ 
  const onClickMapHandler = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    if(!map) { return; }

    // 書き直しのために、マーカーをクリックして削除する
    const features = map.queryRenderedFeatures(e.point, {
      layers: [DISTANCE_POINT_LAYER]
    });
    if (geojson.features.length > 1) { geojson.features.pop(); }

    // マーカーをクリックして削除
    if (features.length) {
      const id = features[0].properties.id;
      geojson.features = geojson.features.filter((point: Feature) => {
        return point.properties && point.properties.id !== id;
      });

    } else {
      const point: Feature<Point> = {
        'type': 'Feature',
        'geometry': {
          'type': 'Point',
          'coordinates': [e.lngLat.lng, e.lngLat.lat]
        },
        'properties': {
          'id': String(new Date().getTime())
        }
      };
      geojson.features.push(point);
    }

    if (geojson.features.length > 1) {
      lineString.geometry.coordinates = geojson.features.map(
        (point: anyType) => {
          return (point.geometry as Point).coordinates;
        }
      );

      geojson.features.push(lineString);

      const distanceKm = turf.length(lineString);
      let distanceLabel;

      // もし1km 未満の場合はメートルで表示
      if (turf.length(lineString) < 1) {
        // メートルで表示
        distanceLabel = `合計距離: ${(distanceKm * 1000).toLocaleString('ja-JP', { maximumFractionDigits: 2 })}m`;
      } else {
        // キロメートルで表示
        distanceLabel = `合計距離: ${distanceKm.toLocaleString('ja-JP', { maximumFractionDigits: 2 })}km`;
      }
      setDistanceLabel(distanceLabel);

      // 中心にラベルを表示
      const centroid = turf.centroid(lineString).geometry.coordinates;
      showMeasureLabel(map, distanceLabel, centroid);
    }
    // ソースを更新
    updateResultLabelSource(map, DISTANCE_SOURCE, geojson);
  }, [map]);


  /* ****************
   * フラグ切り替え時の処理
   * ****************/ 
  useEffect(() => {
    if (!map) { return; }
  
    if(isOpen) {
      // 描画のためのソースとレイヤーを表示
      getLayersBySourceId(map, DISTANCE_SOURCE).forEach((layerId) => {
        map.setLayoutProperty(layerId, 'visibility', 'visible');
      });
      map.on('click', onClickMapHandler);
      
    } else {
      map.off('click', onClickMapHandler);

      // 描画のためのソースとレイヤーを非表示
      geojson.features = [];
      updateResultLabelSource(map, DISTANCE_SOURCE, geojson);

      getLayersBySourceId(map, DISTANCE_SOURCE).forEach((layerId) => {
        map.setLayoutProperty(layerId, 'visibility', 'none');
      });

      // 計測結果ラベルを非表示
      hiddenMeasureLabel(map);

      setDistanceLabel(INITIAL_DISTANCE_LABEL);
    }

    return () => {
      map.off('click', onClickMapHandler);
    }

  }, [isOpen, map, onClickMapHandler]);


  // 距離ラベルを返す
  return isOpen ? distanceLabel : undefined;
}
